import { useQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { PageResponseModel } from "app/data/common/models";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import { PlanningFreightProbill } from "app/data/linehaul-outbound-planning/models";
import { PickupsResponseContentModel } from "app/data/pickup/models";
import { PICKUP_FREIGHT_TYPES, ShipmentListCategory } from "../shipmentListTypes";
import { mapPickupsToPlanningFreight } from "../utils/mapPickupToPlanningFreight";
import { enrichPlanningFreightWithPriorityCustomers } from "../utils/enrichPlanningFreightWithPriorityCustomers";
import { PLANNING_LIST_QUERY_OPTIONS } from "./planningListQueryOptions";

function normalizeDestinationTerminalNumbers(
  destinationTerminalNumbers: number[] | undefined
): number[] {
  if (!destinationTerminalNumbers?.length) return [];
  return [...new Set(destinationTerminalNumbers)].sort((a, b) => a - b);
}

async function fetchAllInboundPickups(params: {
  originTerminalNumber: number;
  destinationTerminalNumber: number;
  freightTypes: string[];
}): Promise<unknown[]> {
  const all: unknown[] = [];
  let page = 0;
  let last = false;

  while (!last) {
    const response = await apiQueryAdapter.query<
      PageResponseModel<PickupsResponseContentModel>
    >("/pickups/inbound", "GET", {
      params: {
        page,
        originTerminalCode: params.originTerminalNumber,
        destinationTerminalCode: params.destinationTerminalNumber,
        freightTypes: params.freightTypes.join(","),
      },
    });
    all.push(...(response.data.content ?? []));
    last = response.data.last ?? true;
    page += 1;
  }

  return all;
}

function planningFreightRowKey(row: PlanningFreightProbill, index: number): string {
  if (row.pickupNumber) return `pickup:${row.pickupNumber}`;
  if (row.pickupId) return `pickupId:${row.pickupId}`;
  if (row.probillNumber > 0) return `probill:${row.probillNumber}`;
  return `row:${index}`;
}

function dedupePlanningFreight(rows: PlanningFreightProbill[]): PlanningFreightProbill[] {
  const seen = new Set<string>();
  return rows.filter((row, index) => {
    const key = planningFreightRowKey(row, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function usePlanningPickupList(
  category: ShipmentListCategory,
  originTerminalNumber: number | undefined,
  destinationTerminalNumbers: number[] | undefined,
  fetchId: string | undefined,
  enabled: boolean
) {
  const freightTypes =
    category in PICKUP_FREIGHT_TYPES
      ? PICKUP_FREIGHT_TYPES[category as keyof typeof PICKUP_FREIGHT_TYPES]
      : [];
  const terminals = normalizeDestinationTerminalNumbers(destinationTerminalNumbers);

  return useQuery({
    queryKey: [
      QUERY_KEYS.OUTBOUND_PLANNING_PICKUP_LIST,
      category,
      originTerminalNumber,
      terminals,
      freightTypes,
      fetchId,
    ],
    queryFn: async () => {
      const results = await Promise.all(
        terminals.map(async (destinationTerminalNumber) => {
          const pickups = await fetchAllInboundPickups({
            originTerminalNumber: originTerminalNumber!,
            destinationTerminalNumber,
            freightTypes,
          });
          return mapPickupsToPlanningFreight(pickups);
        })
      );
      const rows = dedupePlanningFreight(results.flat());
      // Enrich pickup rows with MySQL priorityCustomer for ★ display
      return enrichPlanningFreightWithPriorityCustomers(rows);
    },
    enabled:
      enabled &&
      !!fetchId &&
      freightTypes.length > 0 &&
      typeof originTerminalNumber === "number" &&
      terminals.length > 0,
    ...PLANNING_LIST_QUERY_OPTIONS,
  });
}
